import { HeaderContext, Table, flexRender } from '@tanstack/react-table';
import { CogIcon } from '@heroicons/react/24/solid';
import { MultiSelect } from '../multi-select';

import { useTranslation } from 'react-i18next';

export interface ColumnDefMeta {
  hideColumnVisibility?: boolean;
}

export const ColumnVisibility = <T,>({ table }: { table: Table<T> }) => {
  const { t } = useTranslation('designSystem');
  return (
    <div>
      <MultiSelect
        label=""
        // The cog is right-aligned in the table heading, so the panel opens
        // back over the table rather than off its right-hand edge.
        placement="bottom-end"
        value={table
          .getAllLeafColumns()
          .filter((x) => x.getIsVisible())
          .map((x) => x.id)}
        trigger={
          <button className="flex rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 p-2 py-2 text-gray-600 dark:text-gray-400">
            <CogIcon className="h-4 w-4" />{' '}
          </button>
        }
        displaySelected={false}
        allowSearch
        showSelectAll
        title={t('columnVisibility')}
        setValue={(values) => {
          const fixedColumns = table
            .getAllLeafColumns()
            .filter((x) => x.getCanHide())
            .map((x) => x.id);

          table.setColumnVisibility((oldValues) => {
            const disableOld = Object.fromEntries([
              ...Object.entries(oldValues).map(([key]) => {
                return [key, !fixedColumns.includes(key)];
              }),
              ...fixedColumns.map((item) => [item, false]),
            ]);
            return values.reduce((acc, item) => {
              return {
                ...acc,
                [item]: true,
              };
            }, disableOld);
          });
        }}
        options={table
          .getAllColumns()
          .filter(
            (column) =>
              !(column.columnDef.meta as ColumnDefMeta)?.hideColumnVisibility,
          )
          .map((column) => {
            return {
              value: column.id,
              label: flexRender(
                column.columnDef.header,
                {} as HeaderContext<T, unknown>,
              ),
              disabled: !column.getCanHide(),
            };
          })}
      />
    </div>
  );
};
